import Typography from '@/common/components/base/typography/Typography';
import useInfinityScroll from '@/common/hooks/useInfinityScroll';
import useModal from '@/common/hooks/useModal';
import { FILE_DETAIL_LINKS } from '@/modules/FileDetail/FileDetailLinks';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';
import UnsettledIcon from '../../assets/icons/walletUnsettled.svg?react';
import { UNSETTLED_FILES_INFO } from '../../data/wallet.data';
import { walletPeriodSummaryQueryOptions } from '../../queries/walletPeriodSummary/walletPeriodSummaryQueryOptions';
import { walletPeriodUnsettledQueryOptions } from '../../queries/walletPeriodUnsettled/walletPeriodUnsettledQueryOptions';
import LoadingProjectList from '../loadingCards/LoadingProjectList';
import WalletInfoModal from '../modals/WalletInfoModal';
import ProjectCard from '../walletCards/ProjectCard';
import WalletCountBadge from '../walletCards/WalletCountBadge';
import WalletEmptyState from '../walletCards/WalletEmptyState';
import WalletInfoButton from '../walletCards/WalletInfoButton';

export default function WalletCaseList({
  year,
  month,
  scrollRoot,
}: {
  year: number;
  month: number;
  scrollRoot: HTMLElement | null;
}) {
  const navigate = useNavigate();
  const infoModal = useModal({});

  const { data: summary } = useQuery(
    walletPeriodSummaryQueryOptions({ year, month }),
  );

  const {
    data: fileList,
    isError,
    isPending,
    fetchNextPage,
  } = useInfiniteQuery(walletPeriodUnsettledQueryOptions({ year, month }));

  const { observerRefCallback } = useInfinityScroll(fetchNextPage, scrollRoot);

  if (isError) return <Typography> خطا در بارگیری!</Typography>;

  const listCount = fileList?.pages?.[0]?.count ?? 0;
  const totalCount = summary?.unsettled.count ?? listCount;

  return (
    <Flex flexDirection="column" gap={16} mt={16}>
      <Flex alignItems="center" gap={8} height={26}>
        <UnsettledIcon />
        <Typography textStyle={'h1'} lineHeight={'28px'} color="black">
          تسویه‌نشده ‌ها
        </Typography>
        <WalletInfoButton onClick={infoModal.openModal} />
        {(!!summary || !isPending) && <WalletCountBadge count={totalCount} />}
      </Flex>

      <Flex flexDirection="column" gap={12} pb={0}>
        {!isPending ? (
          fileList?.pages?.map((page, pageIndex) =>
            page?.results?.map((file, fileIndex) => {
              const isLastItem =
                pageIndex === fileList.pages.length - 1 &&
                fileIndex === page.results.length - 1;

              return (
                <Box
                  key={file.revenue_id}
                  width="100%"
                  cursor={'pointer'}
                  ref={isLastItem ? observerRefCallback : null}
                  onClick={() => {
                    navigate(FILE_DETAIL_LINKS.detail(file.file_id));
                  }}
                >
                  <ProjectCard
                    amount={file.amount}
                    referenceId={file.reference_id}
                    fileCreatedAt={file.file_created_at}
                    sentToInsurerAt={file.sent_to_insurer_at ?? null}
                  />
                </Box>
              );
            }),
          )
        ) : (
          <LoadingProjectList />
        )}
      </Flex>

      <WalletInfoModal
        open={infoModal.open}
        closeModal={infoModal.closeModal}
        {...UNSETTLED_FILES_INFO}
      />

      {listCount <= 0 && !isPending && (
        <Box mb="80px">
          <WalletEmptyState
            title="پرونده تسویه‌نشده‌ای در این دوره ندارید"
            description="پرونده‌هایی که از روز 26ام تا پایان ماه به بیمه‌گر ارسال می‌شوند، در دوره بعد قابل مشاهده‌اند."
          />
        </Box>
      )}
    </Flex>
  );
}
